import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useCourierInfo } from './hooks/useCourierInfo';
import { useBikeDetails } from '@/hooks/useBikeDetails';
import { toast } from '@/components/ui/sonner';
import { useQueryClient } from '@tanstack/react-query';

const MySim: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { courierId, courierName, bikeId, phone, loading, error: courierError } = useCourierInfo();
  const { bikeDetails, loading: bikeLoading, error: bikeError } = useBikeDetails(bikeId);
  
  useEffect(() => {
    console.log('MySim страница загружена');
    console.log('courierId:', courierId);
    console.log('bikeId:', bikeId);
  }, [courierId, bikeId]);
  
  // Функция для преобразования GUID в десятичный формат с 4 нулями в начале
  const formatIdToDecimal = (id: string | null): string => {
    if (!id) return '0000-00000';
    
    // Удаляем все дефисы из GUID
    const cleanId = id.replace(/-/g, '');
    
    // Преобразуем первую часть GUID (8 символов) в десятичное число
    // и добавляем 4 нуля в начало
    try {
      const hexPart = cleanId.substring(0, 8);
      const decimalValue = parseInt(hexPart, 16).toString();
      return '0000' + decimalValue;
    } catch (e) {
      console.error('Ошибка преобразования ID:', e);
      return '0000-00000';
    }
  };
  
  // Генерируем данные для отображения
  const simData = React.useMemo(() => {
    // Формат даты оплаты: 21 мая 2023
    const nextPaymentDate = new Date();
    nextPaymentDate.setDate(nextPaymentDate.getDate() + 30);
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
    const nextPaymentFormatted = nextPaymentDate.toLocaleDateString('ru-RU', options);
    
    // Случайные данные для имитации 
    const firmwareVersion = `${Math.floor(Math.random() * 3) + 1}.${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 100)}`;
    
    return {
      subscriptionActive: true,
      nextPayment: nextPaymentFormatted,
      objectId: formatIdToDecimal(courierId),
      bikeModel: bikeDetails?.model || 'Тук-Тук e-bike',
      bikeSerial: bikeDetails?.serial_number || 'SN-' + Math.floor(100000 + Math.random() * 900000),
      simId: bikeDetails?.gps_tag || 'SIM-' + Math.floor(10000 + Math.random() * 90000),
      firmwareVersion: firmwareVersion
    };
  }, [courierId, bikeDetails]);
  
  // Обработчик обновления данных
  const handleRefresh = () => {
    console.log('Обновление данных MySim');
    queryClient.invalidateQueries({ queryKey: ['courierInfo'] });
    queryClient.invalidateQueries({ queryKey: ['bikeDetails', bikeId] });
    toast.success('Данные обновлены');
  };
  
  // Обработчики для кнопок
  const handleBack = () => {
    navigate('/courier');
  };
  
  const handleChangeSubscription = () => {
    toast.info("Изменение подписки недоступно в данный момент");
  };
  
  const handleCancelSubscription = () => {
    toast.info("Отключение подписки недоступно в данный момент");
  };
  
  const handleTurnOn = () => {
    toast.success("Команда активации отправлена на устройство");
  };
  
  const handleTurnOff = () => {
    toast.success("Команда деактивации отправлена на устройство");
  };
  
  if (courierError || bikeError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Мой СИМ</h1>
          <Button
            variant="outline"
            className="flex items-center gap-1"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Назад</span>
          </Button>
        </div>
        
        <Card className="bg-rose-50 border-rose-200">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-rose-100 p-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-rose-700">Ошибка загрузки данных</h2>
              <p className="text-rose-600">Не удалось загрузить информацию о вашем устройстве. Пожалуйста, попробуйте обновить страницу или вернитесь позже.</p>
              <Button 
                variant="outline" 
                className="mt-4 border-rose-300 text-rose-700 hover:bg-rose-100" 
                onClick={handleRefresh}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Обновить данные
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (loading || bikeLoading) {
    return (
      <div className="flex flex-col space-y-4 items-center justify-center h-full py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="text-muted-foreground">Загрузка данных...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Заголовок с кнопкой возврата */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Мой СИМ</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex items-center gap-1"
            onClick={handleRefresh}
          >
            <RefreshCw className="h-4 w-4" />
            <span className="sr-only md:not-sr-only md:inline-flex">Обновить</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center gap-1"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only md:not-sr-only md:inline-flex">Назад</span>
          </Button>
        </div>
      </div>
      
      {/* Основной контент */}
      <Card className="bg-background border-none shadow-lg rounded-xl overflow-hidden">
        <CardContent className="p-0">
          {/* Блок информации о подписке */}
          <div className="bg-green-500 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Подписка оформлена</h2>
                <p className="opacity-90 text-sm mt-1">
                  Следующее списание: {simData.nextPayment}
                </p>
              </div>
              <div className="rounded-full bg-white/20 p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
          
          {/* Управление подпиской */}
          <div className="p-6">
            <h3 className="text-lg font-medium mb-4">Управление подпиской</h3>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={handleChangeSubscription}
              >
                Изменить
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 text-destructive border-destructive hover:bg-destructive/10" 
                onClick={handleCancelSubscription}
              >
                Отключить
              </Button>
            </div>
          </div>
          
          <Separator />
          
          {/* Информация об объекте */}
          <div className="p-6">
            <h3 className="text-lg font-medium mb-4">Информация об объекте</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">ID курьера</span>
                <span className="font-medium">{simData.objectId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Имя</span>
                <span className="font-medium">{courierName || 'Курьер'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Номер телефона</span>
                <span className="font-medium">{phone || '+7 (900) XXX-XX-XX'}</span>
              </div>
            </div>
          </div>
          
          <Separator />
          
          {/* Транспортное средство */}
          <div className="p-6">
            <h3 className="text-lg font-medium mb-4">Транспортное средство</h3>
            
            <div className="flex items-start space-x-4">
              {/* Заменяем SVG-иконку велосипеда на изображение из указанного URL */}
              <div className="rounded-lg bg-muted/40 p-3 flex-shrink-0">
                <img 
                  src="https://urwzhudyitjovstzqfdj.supabase.co/storage/v1/object/public/pics//mySim_courier_icon.png" 
                  alt="СИМ-карта" 
                  className="h-10 w-10 object-contain"
                />
              </div>
              
              {/* Детали транспортного средства */}
              <div className="flex-1 space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Модель</span>
                  <span className="font-medium">{simData.bikeModel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Номер ТС</span>
                  <span className="font-medium">{simData.bikeSerial}</span>
                </div>
              </div>
            </div>
          </div>
          
          <Separator />
          
          {/* IoT-модуль */}
          <div className="p-6">
            <h3 className="text-lg font-medium mb-4">IoT-модуль</h3>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Серия IoT</span>
                <span className="font-medium">{simData.simId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Версия прошивки</span>
                <span className="font-medium">{simData.firmwareVersion}</span>
              </div>
            </div>
            
            {/* Кнопки управления */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="border-green-500 text-green-500 hover:bg-green-500/10"
                onClick={handleTurnOn}
              >
                Включить
              </Button>
              <Button 
                variant="outline" 
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={handleTurnOff}
              >
                Выключить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MySim;
